import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { 
  Package, IndianRupee, Clock, CheckCircle, XCircle, RotateCcw, 
  Truck, PackageCheck, Eye, MapPin, User, Phone
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderHistoryPage({ user }) {
  const navigate = useNavigate();
  const { addToCart, clearCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchProducts();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    setIsLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}/details`);
      setOrderDetails(response.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleViewDetails = async (order) => {
    setSelectedOrder(order);
    setIsDetailsOpen(true);
    await fetchOrderDetails(order.order_id);
  };

  const handleReorder = (order) => {
    clearCart();
    let addedCount = 0;
    
    order.items.forEach(item => {
      const product = products.find(p => p.product_id === item.product_id);
      if (product) {
        for (let i = 0; i < item.quantity; i++) {
          addToCart(product);
        }
        addedCount += item.quantity;
      }
    });
    
    if (addedCount > 0) {
      toast.success(`${addedCount} items added to cart`);
      navigate('/ordering');
    } else {
      toast.error("Some products are no longer available");
    }
  };

  // Order status helpers
  const getOrderStatusInfo = (status) => {
    switch (status) {
      case 'confirmed':
        return { 
          label: 'Order Placed', 
          color: 'bg-blue-100 text-blue-700 border-blue-200',
          icon: <CheckCircle className="w-4 h-4" />,
          description: 'Your order has been received'
        };
      case 'processing':
        return { 
          label: 'Processing', 
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
          icon: <Clock className="w-4 h-4" />,
          description: 'Your order is being prepared'
        };
      case 'dispatched':
        return { 
          label: 'Dispatched', 
          color: 'bg-green-100 text-green-700 border-green-200',
          icon: <Truck className="w-4 h-4" />,
          description: 'Your order is on the way'
        };
      case 'delivered':
        return { 
          label: 'Delivered', 
          color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
          icon: <PackageCheck className="w-4 h-4" />,
          description: 'Order delivered successfully'
        };
      case 'cancelled':
        return { 
          label: 'Cancelled', 
          color: 'bg-red-100 text-red-700 border-red-200',
          icon: <XCircle className="w-4 h-4" />,
          description: 'Order was cancelled'
        };
      default:
        return { 
          label: 'Order Placed', 
          color: 'bg-blue-100 text-blue-700 border-blue-200',
          icon: <CheckCircle className="w-4 h-4" />,
          description: 'Your order has been received'
        };
    }
  };

  // Status timeline component
  const StatusTimeline = ({ currentStatus }) => {
    const statuses = ['confirmed', 'processing', 'dispatched'];
    const currentIndex = statuses.indexOf(currentStatus);
    
    if (currentStatus === 'cancelled') {
      return (
        <div className="flex items-center justify-center py-4">
          <Badge className="bg-red-100 text-red-700 border-red-200 px-4 py-2">
            <XCircle className="w-4 h-4 mr-2" />
            Order Cancelled
          </Badge>
        </div>
      );
    }

    if (currentStatus === 'delivered') {
      return (
        <div className="flex items-center justify-center py-4">
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 px-4 py-2">
            <PackageCheck className="w-4 h-4 mr-2" />
            Order Delivered
          </Badge>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between py-4 px-2">
        {statuses.map((status, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          const statusInfo = getOrderStatusInfo(status);
          
          return (
            <div key={status} className="flex flex-col items-center flex-1">
              <div className="flex items-center w-full">
                {/* Line before */}
                {index > 0 && (
                  <div className={`flex-1 h-1 ${index <= currentIndex ? 'bg-primary' : 'bg-gray-200'}`} />
                )}
                
                {/* Status circle */}
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center border-2
                  ${isCompleted ? 'bg-primary border-primary text-white' : 'bg-white border-gray-300 text-gray-400'}
                  ${isCurrent ? 'ring-4 ring-primary/20' : ''}
                `}>
                  {statusInfo.icon}
                </div>
                
                {/* Line after */}
                {index < statuses.length - 1 && (
                  <div className={`flex-1 h-1 ${index < currentIndex ? 'bg-primary' : 'bg-gray-200'}`} />
                )}
              </div>
              
              <span className={`mt-2 text-xs font-medium text-center ${isCompleted ? 'text-primary' : 'text-gray-400'}`}>
                {statusInfo.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden">
      <Sidebar user={user} activePath="/orders" />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64 overflow-x-hidden">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground mb-1 sm:mb-2">Order History</h1>
          <p className="text-muted-foreground text-sm sm:text-base">View all your past orders and track their status</p>
        </div>

        {/* Orders List */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="spinner"></div>
          </div>
        ) : orders.length === 0 ? (
          <Card className="border-0 shadow-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Package className="w-16 h-16 text-muted-foreground opacity-50 mb-4" />
              <h3 className="font-heading text-xl mb-2">No Orders Yet</h3>
              <p className="text-muted-foreground">Your order history will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {orders.map((order, index) => {
              const statusInfo = getOrderStatusInfo(order.status);
              
              return (
                <Card 
                  key={order.order_id}
                  className="border-0 shadow-card animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`order-${order.order_id}`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                          {order.order_id}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                        {/* Order Status Badge */}
                        <Badge 
                          className={`px-3 py-1 rounded-full flex items-center gap-1 ${statusInfo.color}`}
                          data-testid={`order-status-${order.order_id}`}
                        >
                          {statusInfo.icon}
                          {statusInfo.label}
                        </Badge>
                        
                        <span className="font-heading text-xl sm:text-2xl flex items-center">
                          <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5" />
                          {order.total_amount.toLocaleString('en-IN')}
                        </span>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-primary text-primary hover:bg-primary hover:text-white"
                          onClick={() => handleViewDetails(order)}
                          data-testid={`view-details-${order.order_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Track Order
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-gray-300 text-gray-600 hover:bg-gray-100"
                          onClick={() => handleReorder(order)}
                          data-testid={`reorder-${order.order_id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reorder
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {/* Status Timeline Preview */}
                  <CardContent className="pt-0 pb-4">
                    <StatusTimeline currentStatus={order.status || 'confirmed'} />
                  </CardContent>
                  
                  <CardContent className="pt-0">
                    <div className="bg-muted/30 rounded-xl p-4">
                      <h4 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">
                        Items ({order.items.length})
                      </h4>
                      <div className="space-y-3">
                        {order.items.map((item, i) => (
                          <div 
                            key={i}
                            className="flex items-center justify-between py-2 border-b border-border last:border-0"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                <Package className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-semibold">{item.product_name}</p>
                                <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                              </div>
                            </div>
                            <span className="font-semibold flex items-center">
                              <IndianRupee className="w-4 h-4" />
                              {(item.price * item.quantity).toLocaleString('en-IN')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Order Details Dialog */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Order Details - {selectedOrder?.order_id}
              </DialogTitle>
            </DialogHeader>
            
            {isLoadingDetails ? (
              <div className="flex justify-center items-center py-12">
                <div className="spinner"></div>
              </div>
            ) : orderDetails ? (
              <div className="space-y-6">
                {/* Status Timeline */}
                <div className="bg-muted/30 rounded-xl p-4">
                  <h4 className="font-semibold mb-2">Order Status</h4>
                  <StatusTimeline currentStatus={orderDetails.order?.status || 'confirmed'} />
                  <p className="text-center text-sm text-muted-foreground mt-2">
                    {getOrderStatusInfo(orderDetails.order?.status || 'confirmed').description}
                  </p>
                </div>

                {/* Dispatch Details */}
                {orderDetails.dispatch_details && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-700">
                      <Truck className="w-5 h-5" />
                      Dispatch Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {orderDetails.dispatch_details.vehicle_number && (
                        <div>
                          <span className="text-muted-foreground">Vehicle:</span>
                          <p className="font-medium">{orderDetails.dispatch_details.vehicle_number}</p>
                        </div>
                      )}
                      {orderDetails.dispatch_details.driver_name && (
                        <div>
                          <span className="text-muted-foreground">Driver:</span>
                          <p className="font-medium">{orderDetails.dispatch_details.driver_name}</p>
                        </div>
                      )}
                      {orderDetails.dispatch_details.transport_medium && (
                        <div>
                          <span className="text-muted-foreground">Transport:</span>
                          <p className="font-medium capitalize">{orderDetails.dispatch_details.transport_medium}</p>
                        </div>
                      )}
                      {orderDetails.dispatch_details.dispatched_at && (
                        <div>
                          <span className="text-muted-foreground">Dispatched At:</span>
                          <p className="font-medium">
                            {new Date(orderDetails.dispatch_details.dispatched_at).toLocaleString('en-IN')}
                          </p>
                        </div>
                      )}
                    </div>
                    {orderDetails.dispatch_details.notes && (
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <span className="text-muted-foreground text-sm">Notes:</span>
                        <p className="text-sm">{orderDetails.dispatch_details.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Delivery Address */}
                {orderDetails.order?.delivery_address && (
                  <div className="bg-muted/30 rounded-xl p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      Delivery Address
                    </h4>
                    <div className="text-sm space-y-1">
                      {orderDetails.order.delivery_address.name && (
                        <p className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          {orderDetails.order.delivery_address.name}
                        </p>
                      )}
                      {orderDetails.order.delivery_address.phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          {orderDetails.order.delivery_address.phone}
                        </p>
                      )}
                      <p className="text-muted-foreground">
                        {orderDetails.order.delivery_address.address_line1}
                        {orderDetails.order.delivery_address.address_line2 && `, ${orderDetails.order.delivery_address.address_line2}`}
                        {orderDetails.order.delivery_address.city && `, ${orderDetails.order.delivery_address.city}`}
                        {orderDetails.order.delivery_address.state && `, ${orderDetails.order.delivery_address.state}`}
                        {orderDetails.order.delivery_address.pincode && ` - ${orderDetails.order.delivery_address.pincode}`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Order Items */}
                <div className="bg-muted/30 rounded-xl p-4">
                  <h4 className="font-semibold mb-3">Order Items</h4>
                  <div className="space-y-3">
                    {orderDetails.order?.items?.map((item, i) => (
                      <div 
                        key={i}
                        className="flex items-center justify-between py-2 border-b border-border last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold">{item.product_name}</p>
                            <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <span className="font-semibold flex items-center">
                          <IndianRupee className="w-4 h-4" />
                          {(item.price * item.quantity).toLocaleString('en-IN')}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
                    <span className="font-semibold">Total Amount</span>
                    <span className="font-heading text-xl flex items-center text-primary">
                      <IndianRupee className="w-5 h-5" />
                      {orderDetails.order?.total_amount?.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>

                {/* Status History */}
                {orderDetails.status_history && orderDetails.status_history.length > 0 && (
                  <div className="bg-muted/30 rounded-xl p-4">
                    <h4 className="font-semibold mb-3">Status History</h4>
                    <div className="space-y-3">
                      {orderDetails.status_history.map((history, i) => (
                        <div key={i} className="flex items-start gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                          <div>
                            <p className="font-medium">
                              {history.previous_status} → {history.new_status}
                            </p>
                            <p className="text-muted-foreground">
                              {new Date(history.updated_at).toLocaleString('en-IN')}
                              {history.updated_by_name && ` by ${history.updated_by_name}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Failed to load order details
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
